import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { TariffDetails } from './TariffDetails'
import { useFetchSingleTariff } from '../../api/use-fetch-single-tariff'
import { TariffDetailsSkeleton } from './TariffDetailsSkeleton'
import { ErrorMessage } from '@e3dc-react/shell/Elements/Error'
import useModals from 'src/Hooks/useModals'
import { useHagerBridgePermissions } from 'src/Libs/e-mobility/shared'

export interface TariffDetailsDialogProps {
  open: boolean
  onClose: () => void
  place: number
  installationId: number
  tariffId: string
}

/**
 * Tariffs details dialog component. Component display details for static and dynamic tariffs.
 * @param - single tariff as TariffModel
 * @param - all other params for dialog from ModalRoot
 */
export const TariffDetailsDialog: React.FC<TariffDetailsDialogProps> = ({ open, place, onClose, installationId, tariffId }) => {
  const { t } = useCustomTranslation()

  const { openEditTariffDialog } = useModals()

  const {
    permissions: { tariffPermissions },
  } = useHagerBridgePermissions()

  const { tariff, isError, isLoading, isSuccess, isRefetching } = useFetchSingleTariff(installationId, tariffId)

  const onClickEdit = (): void => {
    onClose()
    openEditTariffDialog({ tariff, installationId: Number(installationId), tariffId })
  }

  return (
    <Dialog
      id="e-mobility-tariff-details"
      maxWidth="sm"
      onClose={onClose}
      title={tariff.tariffName}
      place={place}
      actions={{
        secondary: {
          name: t('common.close'),
        },
        primary: tariffPermissions.editTariff
          ? {
              name: t('common.edit'),
              onClick: onClickEdit,
              disabled: isLoading || isRefetching || isError,
            }
          : undefined,
      }}
    >
      {isError && <ErrorMessage title={t('emobility.tariffs.error-message')} />}
      {(isLoading || isRefetching) && <TariffDetailsSkeleton />}
      {isSuccess && !isRefetching && <TariffDetails tariff={tariff} />}
    </Dialog>
  )
}
